import React from "react";
import { useApp } from "@/context/AppContext";
import { formatIST } from "@/components/Countdown";
import { Gavel, Clock, ShieldAlert, Trophy, Eye, Lock, Mail } from "lucide-react";

export default function Rules() {
  const { config } = useApp();
  const comp = config?.competition;
  const sc = config?.scoring || {};

  const sections = [
    {
      icon: Gavel, title: "Registration",
      points: ["A verified Google account is required.", "Provide a valid name, mobile number and address.", "Choose a unique public display name — your real name and email never appear publicly.", "Duplicate emails, phone numbers or display names are not allowed."],
    },
    {
      icon: Clock, title: "Timing & Unlocks",
      points: [`Competition starts ${comp ? formatIST(comp.start_at) : "15 Aug, 2:00 PM"} IST.`, `${config?.schedule?.length || 10} categories unlock one by one, every ${comp?.interval_minutes || 30} minutes.`, `Each question has a ${sc.question_timer || 60}-second server-side timer.`, "If the timer expires, the question is marked unanswered automatically.", "All unlock and timer decisions are made by the server, not your device clock."],
    },
    {
      icon: Trophy, title: "Scoring & Ties",
      points: [`Correct answer: +${sc.base_points ?? 100} points.`, `Speed bonus: up to +${sc.max_speed_bonus ?? 50} extra points based on remaining time.`, "Wrong or unanswered: 0 points.", "Ties are broken by: highest score → most correct → lowest total time → lowest average response time → earliest final submission."],
    },
    {
      icon: ShieldAlert, title: "Anti-Cheat Policy",
      points: ["Tab switches, window blurs, copy attempts and rapid answering are recorded.", "Leaving the quiz window repeatedly triggers warnings and may lead to penalties.", "Multiple sessions, IP changes and API manipulation attempts are logged for review.", "Disqualification is applied only by the admin after reviewing the event timeline."],
    },
    {
      icon: Lock, title: "Fair Play & Security",
      points: ["Questions are served one at a time; the full question bank never reaches your browser.", "Correct answers are validated only on the server.", "Copying quiz text, using multiple devices or accounts may lead to disqualification."],
    },
    {
      icon: Eye, title: "Privacy",
      points: ["Your Gmail, phone number, address and device data are never shown publicly.", "The public leaderboard only displays your chosen display name, score and stats.", "Analytics are privacy-conscious and used only to run the competition."],
    },
  ];

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="rules-page">
      <div className="text-xs font-bold tracking-[0.3em] text-[#FF9933] uppercase">Fair Play First</div>
      <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tighter text-slate-900 mt-2">Competition Rules</h1>
      {config?.rules_html ? (
        <div className="mt-8 prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: config.rules_html }} />
      ) : (
        <div className="mt-10 space-y-6">
          {sections.map((s) => (
            <div key={s.title} className="bg-white border border-slate-200 rounded-xl p-6 md:p-7">
              <div className="flex items-center gap-3 mb-4">
                <s.icon className="w-5 h-5 text-[#FF9933]" />
                <h2 className="font-heading font-bold text-lg text-slate-900">{s.title}</h2>
              </div>
              <ul className="space-y-2">
                {s.points.map((p, i) => (
                  <li key={i} className="text-sm text-slate-600 flex gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#138808] mt-1.5 shrink-0" />{p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className="mt-10 bg-slate-50 border border-slate-200 rounded-xl p-6 flex items-center gap-4">
        <Mail className="w-5 h-5 text-[#003366] shrink-0" />
        <p className="text-sm text-slate-600">Questions about the rules? Contact <b>{config?.support_email || "support"}</b> or open a ticket on the <a href="/contact" className="text-[#FF9933] font-semibold underline">contact page</a>.</p>
      </div>
    </div>
  );
}
